import { StaffModel, useGetStaffByRoles } from '@urbanx/agx.staff.client'
import { AgxAvatar, SelectChipOption } from '@urbanx/agx.ui-components2'
import { useState, useEffect, useMemo, useContext } from 'react'
import { RolesForTasks } from './RolesForTasks'
import { AuthContext } from '@urbanx/agx.auth.client'

export const unassignedOption = 'Unassigned'

const formatName = (staffMember: StaffModel) => {
  return [staffMember.firstName, staffMember.lastName]
    .filter((namePart) => !!namePart)
    .join(' ')
}

export const useGetAssigneeOptions = (assignedToUserIds?: string[]) => {
  const [staff, setStaff] = useState<SelectChipOption[]>([])
  const { userAccount } = useContext(AuthContext)

  const { getStaffByRoles, getStaffByRolesPending } = useGetStaffByRoles({
    onSuccess: (data) => {
      const sortedStaffMembers = [...(data ?? [])].sort((a, b) =>
        (a.firstName ?? '').localeCompare(b.firstName ?? '')
      )

      const me = sortedStaffMembers.find((s) => s.id === userAccount.agent)
      const remainingStaff = sortedStaffMembers.filter(
        (s) => s.id !== userAccount.agent
      )

      const recombinedStaff = me ? [me, ...remainingStaff] : remainingStaff

      const staffSelectChipOptions =
        recombinedStaff.map((staff) => {
          const hasPhoto = !!staff.photo?.publicPath

          if (hasPhoto) {
            return {
              value: staff.email ?? '',
              label: formatName(staff),
              icon: (
                <AgxAvatar
                  key={staff.email}
                  size="sm"
                  type={'image'}
                  imageUri={staff.photo!.publicPath!}
                />
              ),
            }
          }

          const staffInitials = hasPhoto
            ? undefined
            : [staff.firstName, staff.lastName]
                .map((namePart) => namePart?.[0])
                .filter((namePart) => !!namePart)
                .join('')
                .toUpperCase()

          return {
            value: staff.email ?? '',
            label: formatName(staff),
            icon: (
              <AgxAvatar
                size="sm"
                type={'text-dark'}
                text={staffInitials ?? 'SM'}
              />
            ),
          } as SelectChipOption
        }) ?? ([] as SelectChipOption[])

      setStaff(staffSelectChipOptions)
    },
    onError: (error) => console.error('Failed to fetch staff:', error),
  })

  useEffect(() => {
    getStaffByRoles({ roles: RolesForTasks })
  }, [])

  const assigneeOptions = useMemo(() => {
    return [
      {
        label: unassignedOption,
        value: unassignedOption,
        selected: assignedToUserIds?.includes(unassignedOption) ?? false,
        icon: undefined,
      },
      ...staff.map((s) => ({
        ...s,
        selected: assignedToUserIds?.includes(s.value) ?? false,
      })),
    ]
  }, [assignedToUserIds, staff])

  return { assigneeOptions, assigneeOptionsPending: getStaffByRolesPending }
}
